import React, { useRef, useState } from 'react';

import { usePermissions } from '../../context/PermissionContext';
import { recordLabExternal, collectLabPayment } from '../../services/api/labApi';
import useLabOrderStore from '../../stores/labOrderStore';
import LabReportModal from './LabReportModal';

const TERMINAL = new Set(['completed', 'paid', 'cancelled']);
const EMPTY_REPORT = { open: false, loading: false, error: null, blobUrl: null, contentType: null, fileName: null };

/**
 * LabReportActions — per-order controls shared by the doctor's advise panel,
 * the receptionist's assign panel, and the registration section:
 *   • "Collect ₹X" — bill the in-house lab fee (canonical Payment + GST
 *     receipt); shows a "Paid" badge once collected. Gated on COLLECT_PAYMENT;
 *     hidden for a done-outside (external) lab — those are never billed.
 *   • "Report" — open the latest report (proxied object-URL) when one exists.
 *   • "Done outside" — for a non-terminal order, attach the patient's external
 *     report and complete it (gated on RECORD_LAB_EXTERNAL).
 */
export default function LabReportActions({ order, onChanged, reportOnly = false }) {
  const { hasPermission } = usePermissions();
  // reportOnly = the doctor's review surface: view/download report ONLY, never
  // payment-collect or external-record (those are reception/lab-tech actions).
  const canExternal = !reportOnly && hasPermission('RECORD_LAB_EXTERNAL');
  const canCollect = !reportOnly && hasPermission('COLLECT_PAYMENT');
  const fetchLatestReportBlob = useLabOrderStore((s) => s.fetchLatestReportBlob);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [busyPay, setBusyPay] = useState(false);
  const [payMode, setPayMode] = useState('CASH');
  const [msg, setMsg] = useState(null);
  const [report, setReport] = useState(EMPTY_REPORT);

  const isTerminal = TERMINAL.has(order.status);
  const cost = Number(order.final_cost || 0);

  // Render the report inline (a popup-blocked window.open is why it used to do
  // nothing). Reuses the same fetch + modal as the technician's lab queue.
  const onView = async () => {
    setReport({ ...EMPTY_REPORT, open: true, loading: true });
    const r = await fetchLatestReportBlob(order.lab_order_id);
    if (r.success) {
      setReport({
        open: true, loading: false, error: null,
        blobUrl: r.data.blobUrl, contentType: r.data.contentType, fileName: r.data.fileName,
      });
    } else {
      setReport({ ...EMPTY_REPORT, open: true, loading: false, error: r.error || 'Failed to load report.' });
    }
  };
  const closeReport = () => {
    setReport((rp) => {
      if (rp.blobUrl) { try { URL.revokeObjectURL(rp.blobUrl); } catch (_) { /* noop */ } }
      return EMPTY_REPORT;
    });
  };

  const onExternal = async (file) => {
    setBusy(true);
    setMsg(null);
    const r = await recordLabExternal(order.lab_order_id, file);
    setBusy(false);
    if (r.success) onChanged?.();
    else setMsg(r.error || 'Failed');
  };

  const onCollect = async () => {
    setBusyPay(true);
    setMsg(null);
    const r = await collectLabPayment(order.lab_order_id, payMode);
    setBusyPay(false);
    if (r.success) onChanged?.();
    else setMsg(r.error || 'Failed');
  };

  const btn =
    'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60';
  const fieldCls =
    'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-1.5 py-1';

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {order.is_paid ? (
        <span className="text-xs rounded-full px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 font-semibold">
          💰 Paid{order.receipt_number ? ` #${order.receipt_number}` : ''}
        </span>
      ) : (
        canCollect && !order.is_external && cost > 0 && (
          <>
            <select className={fieldCls} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
            </select>
            <button type="button" onClick={onCollect} disabled={busyPay} className={btn}>
              {busyPay ? 'Collecting…' : `Collect ₹${cost.toFixed(0)}`}
            </button>
          </>
        )
      )}

      {order.report_count > 0 && (
        <button type="button" onClick={onView} className={btn}>
          📄 Report
        </button>
      )}

      {!isTerminal && canExternal && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onExternal(f);
              e.target.value = '';
            }}
          />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={btn}>
            {busy ? 'Saving…' : '🏥 Done outside'}
          </button>
        </>
      )}

      {msg && <span className="text-xs text-rose-600 dark:text-rose-400">{msg}</span>}
      <LabReportModal {...report} onClose={closeReport} />
    </span>
  );
}
